import { forwardRef, type ReactNode } from 'react';

import type { ButtonProps } from '../components/ui/button';
import { Button } from '../components/ui/button';
import { useFormContext } from './use-form.context';

type SubmitButtonProps = Omit<ButtonProps, 'type' | 'children' | 'disabled'> & {
  disabled?: (props: {
    canSubmit: boolean;
    isSubmitting: boolean;
    isValid: boolean;
  }) => boolean;
  children: (props: { canSubmit: boolean; isSubmitting: boolean }) => ReactNode;
};

type ResetButtonProps = Omit<ButtonProps, 'type' | 'children' | 'disabled'> & {
  disabled?: (props: { canSubmit: boolean; isSubmitting: boolean }) => boolean;
  children: (props: { canSubmit: boolean; isSubmitting: boolean }) => ReactNode;
};

const SubmitButton = forwardRef<HTMLButtonElement, SubmitButtonProps>(
  ({ children, ...props }, ref) => {
    const form = useFormContext();

    return (
      <form.Subscribe
        selector={(state) => [
          state.canSubmit,
          state.isSubmitting,
          state.isValid,
        ]}
        children={([canSubmit, isSubmitting, isValid]) => (
          <Button
            ref={ref}
            {...props}
            type='submit'
            disabled={
              props.disabled
                ? props.disabled?.({ canSubmit, isSubmitting, isValid })
                : !canSubmit || isSubmitting || !isValid
            }
          >
            {children({ canSubmit, isSubmitting })}
          </Button>
        )}
      />
    );
  }
);
SubmitButton.displayName = 'SubmitButton';

const ResetButton = forwardRef<HTMLButtonElement, ResetButtonProps>(
  ({ children, ...props }, ref) => {
    const form = useFormContext();

    return (
      <form.Subscribe
        selector={(state) => [state.canSubmit, state.isSubmitting]}
        children={([canSubmit, isSubmitting]) => (
          <Button
            ref={ref}
            {...props}
            type='reset'
            disabled={
              props.disabled
                ? props.disabled?.({ canSubmit, isSubmitting })
                : !canSubmit || isSubmitting
            }
          >
            {children({ canSubmit, isSubmitting })}
          </Button>
        )}
      />
    );
  }
);
ResetButton.displayName = 'ResetButton';

export { ResetButton, SubmitButton };
export type { ResetButtonProps, SubmitButtonProps };
